import React, { useState, useEffect } from "react";
import Breadcrumbs from "../../core/breadcrumbs";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Link } from "react-router-dom";
import { Filter, Sliders, Box } from "react-feather";
import Select from "react-select";
import { apiClient } from "../../services/apiClient";
import { useToast } from "../../Toaster/ToastContext";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";

// Apply the autoTable plugin to jsPDF
applyPlugin(jsPDF);

const PurchaseReport = () => {
  const { showToast } = useToast();
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [purchaseData, setPurchaseData] = useState([]);

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevVisibility) => !prevVisibility);
  };

  // Sort options
  const options = [
    { value: "sortByDate", label: "Sort by Date" },
    { value: "140923", label: "14 09 23" },
    { value: "110923", label: "11 09 23" },
  ];

  // Dynamic product options
  const productOptions = [
    { value: "chooseProduct", label: "Choose Product" },
    ...new Set(
      purchaseData.map((item) => ({
        value: item.product_name || "Unknown",
        label: item.product_name || "Unknown",
      }))
    ),
  ];

  // Fetch purchase report
  const fetchPurchaseReport = async () => {
    try {
      const response = await apiClient.post(`/generatePurchaseReport`, {
        type: "preview",
      });
      if (response?.success && Array.isArray(response.data)) {
        setPurchaseData(response.data);
        if (response.data.length === 0) {
          showToast("No purchase data found", "warning");
        }
      } else {
        setPurchaseData([]);
        showToast("No purchase data found", "warning");
      }
    } catch (error) {
      console.error("Failed to fetch purchase report:", error);
      showToast("Failed to fetch purchase report", "error");
    }
  };

  useEffect(() => {
    fetchPurchaseReport();
  }, []);

  // Handle Excel export
  const handleExportExcel = async () => {
    try {
      const response = await apiClient.post(`/generatePurchaseReport`, {});
      const fileUrl = response?.path;

      if (fileUrl) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = "purchase-report.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast("Purchase report exported to Excel successfully", "success");
      } else {
        showToast("No file URL found in response", "error");
      }
    } catch (error) {
      console.error("Failed to export purchase report to Excel:", error);
      showToast("Failed to export purchase report to Excel", "error");
    }
  };

  // Handle PDF export
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();

      // Header
      doc.setFontSize(20);
      doc.text("Purchase Report", 14, 20);
      doc.setFontSize(12);
      doc.text("Generated on: July 30, 2025, 12:01 PM PKT", 14, 30);

      // Table
      doc.autoTable({
        startY: 40,
        head: [["Product Name", "Purchase Amount", "Purchase Qty", "Purchase Date", "Purchase Reference"]],
        body: purchaseData.map((purchase) => [
          purchase.product_name || "N/A",
          purchase.subtotal ? `$${parseFloat(purchase.subtotal).toFixed(2)}` : "N/A",
          purchase.quantity || "0",
          formatDate(purchase.purchase_date),
          purchase.purchase_reference || "N/A",
        ]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [100, 100, 100], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        margin: { top: 40 },
      });

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(`Page ${i} of ${pageCount}`, 190, 285, { align: "right" });
      }

      // Save PDF
      doc.save("purchase-report.pdf");
      showToast("Purchase report exported to PDF successfully", "success");
    } catch (error) {
      console.error("Failed to export PDF:", error);
      showToast("Failed to export purchase report to PDF", "error");
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Render purchase rows
  const renderPurchaseRows = () => {
    return purchaseData.map((purchase) => (
      <tr key={purchase.purchase_reference || Math.random()}>
        <td>
          <label className="checkboxs">
            <input type="checkbox" />
            <span className="checkmarks" />
          </label>
        </td>
        <td className="productimgname">
          <div className="view-product me-2">
            <Link to="#">
              {purchase.product_image && purchase.product_image.length > 0 ? (
                <ImageWithBasePath src={purchase.product_image[0]} alt="product" />
              ) : (
                <ImageWithBasePath
                  src="assets/img/products/default.png"
                  alt="product"
                />
              )}
            </Link>
          </div>
          <Link to="#">{purchase.product_name || "N/A"}</Link>
        </td>
        <td>{purchase.subtotal ? `$${parseFloat(purchase.subtotal).toFixed(2)}` : "N/A"}</td>
        <td>{purchase.quantity || "0"}</td>
        <td>{formatDate(purchase.purchase_date)}</td>
        <td>{purchase.purchase_reference || "N/A"}</td>
      </tr>
    ));
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <Breadcrumbs
          maintitle="Purchase Report"
          subtitle="Manage Your Purchase Report"
          onExcelClick={handleExportExcel}
          onPDFClick={handleExportPDF}
        />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm formsearch"
                  />
                  <Link to="#" className="btn btn-searchset">
                    <i data-feather="search" className="feather-search" />
                  </Link>
                </div>
              </div>
              <div className="search-path">
                <Link
                  className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}
                  id="filter_search"
                  onClick={toggleFilterVisibility}
                >
                  <Filter className="filter-icon" />
                  <span>
                    <ImageWithBasePath src="assets/img/icons/closes.svg" alt="img" />
                  </span>
                </Link>
              </div>
              <div className="form-sort stylewidth">
                <Sliders className="info-img" />
                <Select
                  className="select"
                  options={options}
                  placeholder="Sort by Date"
                />
              </div>
            </div>
            <div
              className={`card ${isFilterVisible ? "visible" : ""}`}
              id="filter_inputs"
              style={{ display: isFilterVisible ? "block" : "none" }}
            >
              <div className="card-body pb-0">
                <div className="row">
                  <div className="col-lg-3">
                    <div className="input-blocks">
                      <Box className="info-img" />
                      <Select className="select" options={productOptions} />
                    </div>
                  </div>
                  <div className="col-lg-9 col-sm-6 col-12">
                    <div className="input-blocks">
                      <Link to="#" className="btn btn-filters ms-auto">
                        <i data-feather="search" className="feather-search" /> Search
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table datanew">
                <thead>
                  <tr>
                    <th className="no-sort">
                      <label className="checkboxs">
                        <input type="checkbox" id="select-all" />
                        <span className="checkmarks" />
                      </label>
                    </th>
                    <th>Product Name</th>
                    <th>Purchase Amount</th>
                    <th>Purchase Qty</th>
                    <th>Purchase Date</th>
                    <th>Purchase Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseData.length > 0 ? (
                    renderPurchaseRows()
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseReport;